import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Card,
    CardBody,
    IconButton,
    useToast,
    Badge,
    useColorModeValue,
    Input,
    Textarea,
    Divider,
    Alert,
    AlertIcon,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdFitnessCenter } from 'react-icons/md';
import Icon from 'components/icons/Icon';
import { newWorkoutService, DailyWorkout, UpdateDailyWorkoutParams } from 'services/newWorkoutService';
import { Exercise } from 'services/exerciseService';
import ExerciseSelector from 'components/workoutPlanCreator/ExerciseSelector';

interface DailyWorkoutEditorProps {
    isOpen: boolean;
    onClose: () => void;
    dailyWorkout: DailyWorkout | null;
    onWorkoutUpdated: () => void;
}

interface ExerciseWithSettings {
    id?: string; // For existing exercises
    exercise_id: string;
    order_index: number;
    sets: number;
    reps: string;
    weight?: string;
    rest_time?: string;
    notes?: string;
    exercise?: Exercise;
}

const DailyWorkoutEditor: React.FC<DailyWorkoutEditorProps> = ({
    isOpen,
    onClose,
    dailyWorkout,
    onWorkoutUpdated
}) => {
    const [exercises, setExercises] = useState<ExerciseWithSettings[]>([]);
    const [isExerciseSelectorOpen, setIsExerciseSelectorOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const toast = useToast();
    const bgColor = useColorModeValue('white', 'navy.800');
    const borderColor = useColorModeValue('gray.200', 'navy.600');
    const cardBg = useColorModeValue('gray.50', 'navy.700');
    const textColor = useColorModeValue('gray.800', 'white');
    const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
    const inputBg = useColorModeValue('white', 'navy.900');
    const inputBorderColor = useColorModeValue('gray.200', 'navy.500');
    const headerBg = useColorModeValue('gray.50', 'navy.900');

    // Initialize exercises when daily workout changes
    React.useEffect(() => {
        if (dailyWorkout && isOpen) {
            const initialExercises: ExerciseWithSettings[] = dailyWorkout.exercises?.map(ex => ({
                id: ex.id,
                exercise_id: ex.exercise_id,
                order_index: ex.order_index,
                sets: ex.sets,
                reps: ex.reps,
                weight: ex.weight || '',
                rest_time: ex.rest_time || '60s',
                notes: ex.notes || '',
                exercise: ex.exercise
            })) || [];
            setExercises(initialExercises);
        }
    }, [dailyWorkout, isOpen]);

    const handleAddExercises = () => {
        setIsExerciseSelectorOpen(true);
    };

    const handleExercisesSelected = (selectedExercises: Exercise[]) => {
        const newExercises: ExerciseWithSettings[] = selectedExercises.map((exercise, index) => ({
            exercise_id: exercise.id,
            order_index: exercises.length + index,
            sets: 3,
            reps: '10',
            weight: '',
            rest_time: '60s',
            notes: '',
            exercise: exercise
        }));

        setExercises(prev => [...prev, ...newExercises]);
        setIsExerciseSelectorOpen(false);
    };

    const handleRemoveExercise = (index: number) => {
        setExercises(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpdateExercise = (index: number, field: string, value: any) => {
        setExercises(prev => prev.map((ex, i) =>
            i === index ? { ...ex, [field]: value } : ex
        ));
    };

    const handleSaveChanges = async () => {
        if (!dailyWorkout) return;

        setIsUpdating(true);
        try {
            const updateParams: UpdateDailyWorkoutParams = {
                daily_workout_id: dailyWorkout.id,
                exercises: exercises.map((ex, index) => ({
                    id: ex.id, // Include ID for existing exercises
                    exercise_id: ex.exercise_id,
                    order_index: index, // Re-order based on current array order
                    sets: ex.sets,
                    reps: ex.reps,
                    weight: ex.weight,
                    rest_time: ex.rest_time,
                    notes: ex.notes
                }))
            };

            await newWorkoutService.updateDailyWorkout(updateParams);

            toast({
                title: 'Success!',
                description: 'Daily workout updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            onWorkoutUpdated();
            onClose();

        } catch (error) {
            console.error('Error updating daily workout:', error);
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to update daily workout',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancel = () => {
        // Reset exercises to original state
        if (dailyWorkout) {
            const originalExercises: ExerciseWithSettings[] = dailyWorkout.exercises?.map(ex => ({
                id: ex.id,
                exercise_id: ex.exercise_id,
                order_index: ex.order_index,
                sets: ex.sets,
                reps: ex.reps,
                weight: ex.weight || '',
                rest_time: ex.rest_time || '60s',
                notes: ex.notes || '',
                exercise: ex.exercise
            })) || [];
            setExercises(originalExercises);
        }
        onClose();
    };

    if (!dailyWorkout) return null;

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleCancel} size="4xl" scrollBehavior="inside">
                <ModalOverlay bg={useColorModeValue('blackAlpha.300', 'blackAlpha.600')} backdropFilter="blur(10px)" />
                <ModalContent 
                    maxH="90vh" 
                    bg={bgColor}
                    borderColor={borderColor}
                    border="1px"
                >
                    <ModalHeader 
                        borderBottom="1px" 
                        borderColor={borderColor}
                        bg={headerBg}
                        borderTopRadius="md"
                    >
                        <VStack align="start" spacing={2}>
                            <Text fontSize="xl" fontWeight="bold" color={textColor}>
                                Edit {dailyWorkout.title}
                            </Text>
                            <HStack spacing={2}>
                                <Badge colorScheme="blue">
                                    {dailyWorkout.day_of_week}
                                </Badge>
                                <Badge colorScheme="green">
                                    {exercises.length} exercises
                                </Badge>
                                <Badge colorScheme="orange">
                                    {dailyWorkout.estimated_duration} minutes
                                </Badge>
                            </HStack>
                        </VStack>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            {/* Exercise Configuration Info Alert */}
                            <Alert 
                                status="info"
                                bg={useColorModeValue('blue.50', 'navy.900')}
                                borderColor={useColorModeValue('blue.200', 'navy.600')}
                                color={useColorModeValue('blue.800', 'blue.200')}
                                borderRadius="md"
                            >
                                <AlertIcon />
                                <VStack align="start" spacing={1} flex={1}>
                                    <Text fontSize="sm" fontWeight="semibold">
                                        Exercise Configuration Guide
                                    </Text>
                                    <Text fontSize="xs">
                                        • Configure sets, reps, weight, and rest time for each exercise
                                        • Add specific notes for form tips or modifications
                                        • Use "Add Exercises" to include new exercises in this workout
                                    </Text>
                                </VStack>
                            </Alert>
                            
                            {dailyWorkout.is_rest_day ? (
                                <Alert 
                                    status="info"
                                    bg={useColorModeValue('blue.50', 'navy.900')}
                                    borderColor={useColorModeValue('blue.200', 'navy.600')}
                                    color={useColorModeValue('blue.800', 'blue.200')}
                                >
                                    <AlertIcon />
                                    This is a rest day. You cannot add exercises to rest days.
                                </Alert>
                            ) : (
                                <>
                                    {/* Add Exercises Button */}
                                    <Card 
                                        variant="outline"
                                        bg={cardBg}
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack justify="space-between" align="center">
                                                <VStack align="start" spacing={1}>
                                                    <Text fontWeight="semibold" color={textColor}>Exercises</Text>
                                                    <Text fontSize="sm" color={mutedTextColor}>
                                                        Add and configure exercises for this workout
                                                    </Text>
                                                </VStack>
                                                <Button
                                                    colorScheme="blue"
                                                    leftIcon={<Icon as={MdAdd} />}
                                                    onClick={handleAddExercises}
                                                    size="sm"
                                                >
                                                    Add Exercises
                                                </Button>
                                            </HStack>
                                        </CardBody>
                                    </Card>

                                    {/* Exercise List */}
                                    {exercises.length > 0 && (
                                        <VStack align="stretch" spacing={3}>
                                            <Divider />
                                            <Text fontSize="sm" fontWeight="medium" color={mutedTextColor}>
                                                Exercise Configuration ({exercises.length})
                                            </Text>
                                            {exercises.map((exercise, index) => (
                                                <Card 
                                                    key={index} 
                                                    size="sm" 
                                                    variant="outline" 
                                                    bg={cardBg}
                                                    borderColor={borderColor}
                                                    _hover={{
                                                        borderColor: useColorModeValue('blue.300', 'blue.400'),
                                                        boxShadow: useColorModeValue('0 2px 8px rgba(0,0,0,0.1)', '0 2px 8px rgba(0,0,0,0.3)')
                                                    }}
                                                >
                                                    <CardBody>
                                                        <VStack align="stretch" spacing={3}>
                                                            <HStack justify="space-between" align="start">
                                                                <VStack align="start" spacing={1} flex={1}>
                                                                    <Text fontWeight="medium" color={textColor}>
                                                                        {exercise.exercise?.name || `Exercise ${index + 1}`}
                                                                    </Text>
                                                                    {exercise.exercise?.description && (
                                                                        <Text fontSize="xs" color={mutedTextColor} noOfLines={1}>
                                                                            {exercise.exercise.description}
                                                                        </Text>
                                                                    )}
                                                                </VStack>
                                                                <IconButton
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    colorScheme="red"
                                                                    icon={<Icon as={MdDelete} />}
                                                                    onClick={() => handleRemoveExercise(index)}
                                                                    aria-label="Remove exercise"
                                                                />
                                                            </HStack>

                                                            <HStack spacing={4} fontSize="sm" wrap="wrap">
                                                                <VStack align="start" spacing={1}>
                                                                    <Text fontSize="xs" fontWeight="medium" color={textColor}>Sets</Text>
                                                                    <Input
                                                                        size="sm"
                                                                        width="60px"
                                                                        type="number"
                                                                        value={exercise.sets}
                                                                        onChange={(e) =>
                                                                            handleUpdateExercise(index, 'sets', parseInt(e.target.value) || 0)
                                                                        }
                                                                        bg={inputBg}
                                                                        borderColor={inputBorderColor}
                                                                        color={textColor}
                                                                        _focus={{
                                                                            borderColor: 'blue.500',
                                                                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                                                        }}
                                                                    />
                                                                </VStack>
                                                                <VStack align="start" spacing={1}>
                                                                    <Text fontSize="xs" fontWeight="medium" color={textColor}>Reps</Text>
                                                                    <Input
                                                                        size="sm"
                                                                        width="80px"
                                                                        value={exercise.reps}
                                                                        onChange={(e) =>
                                                                            handleUpdateExercise(index, 'reps', e.target.value)
                                                                        }
                                                                        placeholder="10"
                                                                        bg={inputBg}
                                                                        borderColor={inputBorderColor}
                                                                        color={textColor}
                                                                        _placeholder={{ color: mutedTextColor }}
                                                                        _focus={{
                                                                            borderColor: 'blue.500',
                                                                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                                                        }}
                                                                    />
                                                                </VStack>
                                                                <VStack align="start" spacing={1}>
                                                                    <Text fontSize="xs" fontWeight="medium" color={textColor}>Weight</Text>
                                                                    <Input
                                                                        size="sm"
                                                                        width="80px"
                                                                        value={exercise.weight || ''}
                                                                        onChange={(e) =>
                                                                            handleUpdateExercise(index, 'weight', e.target.value)
                                                                        }
                                                                        placeholder="kg"
                                                                        bg={inputBg}
                                                                        borderColor={inputBorderColor}
                                                                        color={textColor}
                                                                        _placeholder={{ color: mutedTextColor }}
                                                                        _focus={{
                                                                            borderColor: 'blue.500',
                                                                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                                                        }}
                                                                    />
                                                                </VStack>
                                                                <VStack align="start" spacing={1}>
                                                                    <Text fontSize="xs" fontWeight="medium" color={textColor}>Rest</Text>
                                                                    <Input
                                                                        size="sm"
                                                                        width="80px"
                                                                        value={exercise.rest_time || ''}
                                                                        onChange={(e) =>
                                                                            handleUpdateExercise(index, 'rest_time', e.target.value)
                                                                        }
                                                                        placeholder="60s"
                                                                        bg={inputBg}
                                                                        borderColor={inputBorderColor}
                                                                        color={textColor}
                                                                        _placeholder={{ color: mutedTextColor }}
                                                                        _focus={{
                                                                            borderColor: 'blue.500',
                                                                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                                                        }}
                                                                    />
                                                                </VStack>
                                                            </HStack>

                                                            <VStack align="start" spacing={1}>
                                                                <Text fontSize="xs" fontWeight="medium" color={textColor}>Exercise Notes</Text>
                                                                <Textarea
                                                                    size="sm"
                                                                    value={exercise.notes || ''}
                                                                    onChange={(e) =>
                                                                        handleUpdateExercise(index, 'notes', e.target.value)
                                                                    }
                                                                    placeholder="Add specific instructions, form tips, modifications, or any special notes for this exercise..."
                                                                    rows={2}
                                                                    resize="vertical"
                                                                    fontSize="xs"
                                                                    bg={inputBg}
                                                                    borderColor={inputBorderColor}
                                                                    color={textColor}
                                                                    _placeholder={{ color: mutedTextColor }}
                                                                    _focus={{
                                                                        borderColor: 'blue.500',
                                                                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                                                    }}
                                                                />
                                                            </VStack>
                                                        </VStack>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </VStack>
                                    )}

                                    {exercises.length === 0 && (
                                        <Alert 
                                            status="info"
                                            bg={useColorModeValue('blue.50', 'navy.900')}
                                            borderColor={useColorModeValue('blue.200', 'navy.600')}
                                            color={useColorModeValue('blue.800', 'blue.200')}
                                        >
                                            <AlertIcon />
                                            No exercises added yet. Click "Add Exercises" to get started.
                                        </Alert>
                                    )}
                                </>
                            )}
                        </VStack>
                    </ModalBody>

                    <ModalFooter 
                        borderTop="1px" 
                        borderColor={borderColor}
                        bg={headerBg}
                        borderBottomRadius="md"
                    >
                        <HStack spacing={3}>
                            <Button variant="outline" onClick={handleCancel} isDisabled={isUpdating}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleSaveChanges}
                                isLoading={isUpdating}
                                loadingText="Saving..."
                                leftIcon={<Icon as={MdFitnessCenter} />}
                                isDisabled={dailyWorkout.is_rest_day}
                            >
                                Save Changes
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <ExerciseSelector
                isOpen={isExerciseSelectorOpen}
                onClose={() => setIsExerciseSelectorOpen(false)}
                onExercisesSelected={handleExercisesSelected}
            />
        </>
    );
};

export default DailyWorkoutEditor;
